export const formatAmount = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '0'
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

const ONE_THOUSAND = 1000

/**
 * Raw funding potential (e.g. "50000.00") → banded label "50K+" for the
 * eligibility program card. Floors to the nearest thousand; the "$" symbol is
 * rendered separately by the card. Returns null below 1K so the card can hide
 * the metric rather than show "0K+".
 */
export const formatFundingBand = (
  value: string | number | null | undefined
): string | null => {
  const amount = typeof value === 'string' ? Number(value) : value
  if (amount == null || Number.isNaN(amount) || amount < ONE_THOUSAND)
    return null
  return `${Math.floor(amount / ONE_THOUSAND)}K+`
}

/** ISO datetime → "Jun 28, 2026" for the wallet "Last updated on" line. */
export const formatWalletDate = (
  iso: string | null | undefined
): string | null => {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** ISO datetime → "Jun 28, 2026 at 9:15 AM" for the wallet history timeline. */
export const formatWalletHistoryTime = (
  iso: string | null | undefined
): string | null => {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  const day = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
  return `${day} at ${time}`
}

// Renders in the viewer's local timezone (Date + toLocale* default to it) but
// deliberately omits the tz abbreviation — the user reads their own wall-clock
// time, so a "GMT"/"IST" suffix only adds noise.
const dayTime = (
  iso: string | null | undefined
): { day: string; time: string } | null => {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  const day = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
  return { day, time }
}

export const formatStageTime = (
  iso: string | null | undefined
): string | null => {
  const parts = dayTime(iso)
  return parts ? `${parts.day} | ${parts.time}` : null
}

export const formatActivityTime = (
  iso: string | null | undefined
): string | null => {
  const parts = dayTime(iso)
  return parts ? `${parts.day} at ${parts.time}` : null
}
